import type { PropertyDeclarations } from 'lit';
import type { ICTMOptions } from '../types/network';
import type {
  IBotNetworkInterface,
  TKbiSettings,
  TPrompt,
  TMessage,
  IBotNetworkSettings
} from '../types/bot_interface';

import { LitElement, html } from 'lit';

import f1 from '@eab-pip/pi-f1';

import { KnowledgeBot } from '../core/knowledge_bot';
import { defaultSecuritySettings } from '../models/security_model';

/**
 * A mixin for the KnowledgeBot
 *
 * @param NavKnowledgeBotElement
 * @constructor
 */
export const NavKnowledgeBotMixin = (NavKnowledgeBotElement: typeof LitElement) =>
  class extends NavKnowledgeBotElement implements Partial<IBotNetworkInterface> {
    /**
     * a title in the header
     * @note we must leave this undefined to support all products which still use title
     * @attr
     */
    heading: string;

    /**
     * a title in the header
     * @attr
     * @deprecated
     * @see heading
     */
    title = 'Knowledge Bot';

    /**
     * if no image is given, avatar will not be displayed
     * if an image is given, then on receive messages an image will be displayed
     * @attr
     */
    image: string;

    /**
     * placeholder text in the textarea where a human will write a message
     * @attr
     */
    placeholder: string;

    /**
     * an initial message from the bot to prompt to the user
     * @attr
     */
    prompt: string;

    /**
     * an array of initial message from the bot to prompt to the user
     * @attr
     */
    prompts: TPrompt[];

    /**
     * a single uri for pulling the legal notice
     */
    legalNoticeUri?: string;

    /**
     * read only boolean value
     */
    readOnly: boolean;

    /**
     * messages that come in from the bot to be displayed
     */
    messages: TMessage[];

    /**
     *
     * assign a function to generate the legal notice uri
     */
    legalNoticeUriFn: Function;

    /**
     * CTM options for the legal_notice authorization URL and token
     * @examples
     *
     * ```
     * # for the endpoint uri
     * /api/v1/core/retrieve_endpoint?service_key=navai&endpoint_name=legal_notice
     * //=> { access_url: '-- entire url we should hit --' }
     *
     * # for the token
     * /api/v1/core/client_jwt
     * //=> { access_token: '-- token --' }
     * ```
     */
    ctmLegalNotice: ICTMOptions = {} as ICTMOptions;

    /**
     * there are three options:
     *
     * - (undefined), we do not handle network
     * - `http`, we send a fetch
     * - `ws`, we send a web socket
     */
    uri?: string;

    /**
     * assign a function to generate the uri
     */
    uriFn: Function;

    /**
     * CTM options for the authorization URL and token
     * @examples
     *
     * ```
     * # for the endpoint uri
     * /api/v1/core/retrieve_endpoint?service_key=navai&endpoint_name=ws
     * //=> { access_url: '-- entire url we should hit --' }
     *
     * # for the token
     * /api/v1/core/client_jwt
     * //=> { access_token: '-- token --' }
     * ```
     */
    ctmWs: ICTMOptions = {} as ICTMOptions;

    /**
     * settings for network communication:
     *
     * - `http` uses headers for fetch
     * - `ws` uses an array of protocols
     */
    uriSettings?: IBotNetworkSettings;

    /**
     * settings to configure the KnowledgeBot
     */
    settings: TKbiSettings = {} as TKbiSettings;

    /**
     * functions to augment behavior at runtime
     */
    substitutions: Record<string, Function[]>;

    /**
     * enables custom network adapters for the bot
     */
    customAdapters = [];

    /**
     * default settings for the bot interface
     */
    readonly settingsDefaults: TKbiSettings = {
      security: defaultSecuritySettings,
      allow: {
        raw_text_input: false,
        user_feedback: true,
        lock_on_send: true
      },
      hidden: {
        loading_indicator: false,
        heading_close: true,
        timestamp: false,
        header: false,
        footer: false,
        reset: false
      },
      labels: {
        textarea_streaming_placeholder: 'Please wait until the response is finished',
        feedback_question_prompt: 'Why did you choose this rating? (optional)',
        feedback_textarea_placeholder: 'Please provide additional feedback',
        feedback_acknowledgement: 'Thank you for your feedback.',
        textarea_placeholder: 'Ask me a question',
        tip_feedback_button_dislike: 'Dislike Response',
        tip_feedback_button_close: 'Close Feedback Box',
        tip_message_button_dislike: 'Dislike Response',
        tip_message_button_copy: 'Copy Response',
        tip_message_button_speak: 'Read Response',
        avatar_a11y_text: 'Avatar Profile',
        tip_button_submit: 'Send Message',
        feedback_button_submit: 'Submit Feedback',
        tip_button_refresh: 'Reset Conversation',
        loading_text: 'Thinking...'
      }
    };

    /**
     * the bot messaging and networking protocol
     */
    bot = new KnowledgeBot();

    static get properties(): PropertyDeclarations {
      return {
        title: {
          type: String
        },
        heading: {
          type: String
        },
        image: {
          type: String
        },
        placeholder: {
          type: String
        },
        prompt: {
          type: String
        },
        prompts: {
          type: Array
        },
        readOnly: {
          type: Boolean,
          attribute: 'read-only',
          reflect: true
        },
        messages: {
          type: Array
        },
        uri: {
          type: String
        },
        legalNoticeUri: {
          type: String,
          attribute: 'legal-notice-uri'
        },
        ctmLegalNotice: {
          type: Object,
          attribute: 'ctm-legal-notice'
        },
        ctmWs: {
          type: Object,
          attribute: 'ctm-ws'
        },
        uriSettings: {
          type: Object,
          attribute: 'uri-settings'
        },
        settings: {
          type: Object
        },
        substitutions: {
          type: Object,
          attribute: false
        },
        customAdapters: {
          type: Array,
          attribute: 'custom-adapters'
        }
      };
    }

    /**
     * shut the bot down, which will abort/stop network connections
     */
    disconnectedCallback() {
      super.disconnectedCallback();

      if (this.bot) {
        this.bot.shutdown();
      }

      // we always want a bot, but we create a new bot to erase all of the history
      //
      this.bot = new KnowledgeBot();
    }

    /**
     * before we render we check if:
     *
     * - `loading` changed and scroll to the bottom
     * - `uri` changed and stop/start the network connection
     */
    update(props: Map<string, unknown>) {
      super.update(props);

      if (props.has('customAdapters')) {
        this.bot.customAdapters = this.customAdapters;
      }

      if (props.has('settings')) {
        this.bot.security.configure(this.settings.security);
      }

      if (props.has('uri')) {
        this.bot.createNetwork(this as IBotNetworkInterface);
      }

      const hasInitialPrompt =
        props.has('ctmLegalNotice') || props.has('legalNoticeUri') || props.has('prompts') || props.has('prompt');
      if (hasInitialPrompt) {
        this._displayInitialMessage();
      }

      if (props.has('substitutions')) {
        this.bot.substitutions = this.substitutions;
      }
    }

    /**
     * clear the bot and create a new network
     * @deprecated use `el.bot.reset()`
     */
    resetNetwork() {
      this.bot.reset();
    }

    /**
     * remove all messages
     * @deprecated
     */
    resetMessages() {
      this.bot.shutdown();
      this.bot.messages.eraseAll();
      this._displayInitialMessage();

      this.requestUpdate();
    }

    /**
     * show initial message - either legal message or static prompt
     */
    protected _displayInitialMessage() {
      if (!f1.is.empty(this.ctmLegalNotice) || !f1.is.empty(this.legalNoticeUri)) {
        this._displayLegalMessage();
      } else {
        this._displayPromptMessage();
      }
    }

    /**
     * show legal notice to the user when they first load the knowledge bot
     */
    protected _displayLegalMessage() {
      const uri = this.legalNoticeUriFn ? this.legalNoticeUriFn() : this.legalNoticeUri;
      const ctm = this.ctmLegalNotice;

      this.bot.pullLegalNotice({ uri, ctm }).then(({ data }: { data: null | { notice: string } }) => {
        if (data) {
          this.bot.insertMessage(data.notice, 'from-bot');
        }
      });
    }

    /**
     * show prompt message to the user using prompt attribute
     */
    protected _displayPromptMessage() {
      if (!f1.is.empty(this.prompt)) {
        this.prompts = [{ html: this.prompt }];
      }

      if (!f1.is.empty(this.prompts)) {
        this.prompts.forEach((prompt: TPrompt) => {
          this.bot.insertMessage(prompt.html, 'from-bot');
        });
      }
    }
  };
